// Demo step 2 (PRD §4.5, US-27): the chain from the product batch back to strain lot and water, each node's values.
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../components/Layout.tsx';
import { Link, useParams } from 'react-router';
import { Enum, ErrorBox, fmtValue, Loading } from '../components/ui.tsx';
import { batchChain, type Step } from '../lib/chain.ts';
import { evaluationsFor, valuesFor } from '../lib/data.ts';
import { useNames } from '../lib/names.ts';
import type { EvaluationRow, ValueRow } from '../lib/types.ts';

// US-24 / T16: the three parties shown as separate blocks. Grouping of data-dictionary fields for display only; the
// fields, their tiers and owners come from config/fields.json, and row-level security decides what a role sees.
const OPERATOR_BLOCKS = [
  {
    key: 'legal_manufacturer',
    fields: [
      'EO_LEGAL_MANUFACTURER_NAME',
      'EO_LEGAL_MANUFACTURER_ROLE',
      'EO_LEGAL_MANUFACTURER_ADDRESS',
      'EO_LEGAL_MANUFACTURER_ID',
    ],
  },
  { key: 'production_operator', fields: ['MANUFACTURING_OPERATOR', 'MFG_FACILITY'] },
  { key: 'cultivation_site', fields: ['CULTIVATION_SITE_ID', 'CULTIVATION_SITE_ADDRESS'] },
] as const;

export function Genealogy() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const [steps, setSteps] = useState<Step[] | null>(null);
  const [values, setValues] = useState<ValueRow[]>([]);
  const [latest, setLatest] = useState<EvaluationRow | null>(null);
  const [error, setError] = useState<unknown>(null);
  const names = useNames();

  useEffect(() => {
    void (async () => {
      try {
        const [{ steps: chain, prodCode: pc }, evals] = await Promise.all([
          batchChain(id),
          evaluationsFor(id),
        ]);
        setLatest(evals.find((e) => e.mode === 'as_is') ?? null);
        // values first: the page renders once both are known, so no block shows "no value" while loading
        setValues(
          await valuesFor([...chain.map((s) => s.id).filter((x): x is string => !!x), ...(pc ? [pc] : [])]),
        );
        setSteps(chain);
      } catch (e) {
        setError(e);
      }
    })();
  }, [id]);

  if (!steps) return <Loading />;
  // Completeness is the server's verdict (R-TRACE-BATCH, which also requires the water link for wastewater products),
  // never recomputed here; without a stored evaluation the page says so instead of guessing.
  const g = latest?.verdict.genealogy;
  return (
    <main>
      <p>
        <Link to={`/batches/${id}`}>← {id}</Link>
      </p>
      <PageHeader title={`${t('genealogy.title')} · ${id}`} page="genealogy" />
      {g ? (
        <p className={g.complete ? 'ok' : 'error'}>
          {g.complete ? t('genealogy.complete') : t('genealogy.incomplete')}
          {g.missing_links.length > 0 &&
            ` · ${t('genealogy.missing')}: ${g.missing_links.map((k) => t(`kind.${k}`, k)).join(', ')}`}
          <span className="muted small">
            {' '}
            · {t('genealogy.from_evaluation')} {latest.evaluation_id} ({latest.verdict.evaluation_date})
          </span>
        </p>
      ) : (
        <p className="muted">{t('genealogy.not_evaluated')}</p>
      )}
      {g && !g.complete && (
        <p className="hint">
          {t('genealogy.missing_help')} <Link to={`/worklist`}>{t('nav.worklist')}</Link>
        </p>
      )}
      <ErrorBox error={error} />
      <h2>{t('operators.title')}</h2>
      <div className="operator-blocks">
        {OPERATOR_BLOCKS.map((b) => (
          <section key={b.key} className="operator-block">
            <h3>{t(`operators.${b.key}`)}</h3>
            <dl>
              {b.fields.map((f) => {
                const v = values.find((x) => x.field_id === f);
                return (
                  <div key={f}>
                    <dt>
                      {names.field(f)} <code className="small muted">{f}</code>
                    </dt>
                    <dd>
                      {v ? (
                        <>
                          {fmtValue(v.value)}{' '}
                          <span className="muted small">
                            (<Enum ns="validation" v={v.validation_status} />)
                          </span>
                        </>
                      ) : (
                        <span className="muted">{t('operators.no_value')}</span>
                      )}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </section>
        ))}
      </div>
      <p className="muted small">{t('operators.note')}</p>
      <ol className="chain">
        {steps.map((s, i) => (
          <li key={`${s.kind}-${i}`}>
            <h3>
              {t(`kind.${s.kind}`)}: {s.id ?? '—'}
              {s.sameAs && (
                <span className="muted small">
                  {' '}
                  ({t('genealogy.same_as', { kind: t(`kind.${s.sameAs}`) })})
                </span>
              )}
            </h3>
            {s.id && (
              <details open={s.kind === 'raw_water'}>
                <summary>
                  {t('genealogy.values', { count: values.filter((v) => v.node_id === s.id).length })}
                </summary>
                <table>
                  <tbody>
                    {values
                      .filter((v) => v.node_id === s.id && v.node_kind === (s.sameAs ?? s.kind))
                      .map((v) => (
                        <tr key={v.value_id}>
                          <td>
                            {names.field(v.field_id)}
                            <div className="small muted">
                              <code>{v.field_id}</code>
                            </div>
                          </td>
                          <td>
                            {fmtValue(v.value)} {v.unit}
                          </td>
                          <td className="small">
                            {v.source_document_ref ? (
                              <Link to={`/evidence?q=${encodeURIComponent(v.field_id)}`}>
                                {v.source_document_ref}
                              </Link>
                            ) : (
                              <span className="muted">—</span>
                            )}
                          </td>
                          <td>
                            <Enum ns="validation" v={v.validation_status} />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </details>
            )}
          </li>
        ))}
      </ol>
    </main>
  );
}
